import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, Archive, ArrowRight, Barcode, CalendarClock, Check, ChevronRight,
  CircleUserRound, ClipboardList, Download, Home, Leaf, ListPlus, LoaderCircle, LogOut, Minus,
  Info, Package, PackagePlus, Pencil, Plus, RotateCcw, Search, ShoppingBasket, Snowflake, Trash2,
  Utensils, Warehouse, X,
} from "lucide-react";
import { useAuth } from "./auth";
import {
  createInventory, createShopping, exportDemoData, isDemoMode, listActivity, listInventory,
  listRecommendations, listShopping, removeShopping, searchProducts, undoActivity, updateInventory,
} from "./api";
import { ItemForm } from "./components/ItemForm";
import { daysUntil, expiryBucket, expiryLabel, matchesExpiryFilter, todayInSydney } from "./lib/expiry";
import { isStockReminderDue, useOneFromStock } from "./lib/stock";
import type { ActivityItem, ExpiryFilter, InventoryInput, InventoryItem, ProductHistoryResult, RecommendationItem, ShoppingItem } from "./types";

type Tab = "home" | "pantry" | "shopping" | "activity";

const navItems: { id: Tab; label: string; icon: typeof Home }[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "pantry", label: "Pantry", icon: Archive },
  { id: "shopping", label: "List", icon: ShoppingBasket },
  { id: "activity", label: "Activity", icon: Activity },
];

const expiryFilters: { id: ExpiryFilter; label: string }[] = [
  { id: "all", label: "All" }, { id: "expired", label: "Expired" }, { id: "today", label: "Today" },
  { id: "3-days", label: "3 days" }, { id: "7-days", label: "7 days" },
  { id: "30-days", label: "30 days" }, { id: "none", label: "No expiry" },
];

const productStatusLabels: Record<ProductHistoryResult["status"], string> = {
  ACTIVE: "In pantry",
  CONSUMED: "Used",
  DISCARDED: "Discarded",
  DELETED: "Deleted",
};

function SignIn() {
  const { signIn } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try { await signIn(username, password); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Sign-in failed."); setBusy(false); }
  }

  return (
    <main className="signin-page">
      <section className="signin-brand">
        <div className="brand-mark large"><Leaf /></div>
        <span className="eyebrow light">THE SAPK HOUSEHOLD</span>
        <h1>Know what’s home.<br /><em>Waste less.</em></h1>
        <p>One shared pantry for Nakul, Rohit and Sap — built for the moment you’re standing at the fridge.</p>
        <div className="signin-feature-row">
          <span><Barcode /> Scan products</span><span><CalendarClock /> Catch expiry</span><span><ClipboardList /> Share one list</span>
        </div>
      </section>
      <section className="signin-card-wrap">
        <form className="signin-card" onSubmit={submit}>
          <div className="mobile-brand"><span className="brand-mark"><Leaf /></span><strong>Sapk Grocery</strong></div>
          <span className="eyebrow">WELCOME HOME</span>
          <h2>Sign in to your pantry</h2>
          <p>Use your household account to continue.</p>
          <label className="field">Username
            <input autoComplete="username" autoCapitalize="none" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Nakul, Rohit or Sap" autoFocus />
          </label>
          <label className="field">Password
            <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Your household password" />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button full signin-submit" disabled={busy || !username || !password}>
            {busy ? <LoaderCircle className="spin" /> : <>Open pantry <ArrowRight /></>}
          </button>
          {isDemoMode && <p className="demo-note">Local preview: use Nakul, Rohit or Sap with <code>grocery@123</code>.</p>}
        </form>
      </section>
    </main>
  );
}

function ExpiryBadge({ date }: { date?: string }) {
  const bucket = expiryBucket(date);
  const tone = bucket === "expired" ? "danger" : bucket === "today" || bucket === "3-days" ? "warning" : bucket === "7-days" ? "soon" : "muted";
  return <span className={`expiry-badge ${tone}`}><span />{expiryLabel(date)}</span>;
}

function LocationIcon({ location }: { location: InventoryItem["location"] }) {
  if (location === "Freezer") return <Snowflake />;
  if (location === "Fridge") return <Warehouse />;
  return <Archive />;
}

function InventoryCard({ item, onEdit, onQuantity, onStatus, onAddToList }: {
  item: InventoryItem;
  onEdit: (item: InventoryItem) => void;
  onQuantity: (item: InventoryItem, quantity: number) => void;
  onStatus: (item: InventoryItem, status: "CONSUMED" | "DISCARDED") => void;
  onAddToList: (item: InventoryItem) => void;
}) {
  const low = isStockReminderDue(item.quantity, item.minimumQuantity);
  return (
    <article className="inventory-card">
      <div className="product-avatar">
        {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <span>{item.productName.charAt(0).toUpperCase()}</span>}
      </div>
      <div className="inventory-main">
        <div className="inventory-title-row">
          <div><h3>{item.productName}</h3><p>{item.brand || item.category}</p></div>
          <button className="icon-button small" onClick={() => onEdit(item)} aria-label={`Edit ${item.productName}`}><Pencil /></button>
        </div>
        <div className="meta-row"><span><LocationIcon location={item.location} />{item.location}</span><ExpiryBadge date={item.expiryDate} />{item.isOpen && <span className="open-label">Pack open</span>}{low && <span className="low-label">Low stock</span>}</div>
        <div className="card-controls">
          <div className="quantity-stepper" aria-label={`Quantity of ${item.productName}`}>
            <button onClick={() => onQuantity(item, Math.max(0, Number((item.quantity - 1).toFixed(2))))} aria-label="Decrease quantity"><Minus /></button>
            <strong>{item.quantity}<small>{item.unit}</small></strong>
            <button onClick={() => onQuantity(item, item.quantity + 1)} aria-label="Increase quantity"><Plus /></button>
          </div>
          <div className="item-actions">
            {low && <button onClick={() => onAddToList(item)}><ListPlus /> Add to list</button>}
            <button onClick={() => onStatus(item, "CONSUMED")}><Utensils /> Used</button>
            <button className="discard" onClick={() => onStatus(item, "DISCARDED")}><Trash2 /> Discard</button>
          </div>
        </div>
      </div>
    </article>
  );
}

function HomeView({ user, inventory, shopping, recommendations, onScan, onTab, onEdit, onQuantity, onStatus, onAddToList }: {
  user: string; inventory: InventoryItem[]; shopping: ShoppingItem[]; recommendations: RecommendationItem[]; onScan: () => void; onTab: (tab: Tab) => void;
  onEdit: (item: InventoryItem) => void; onQuantity: (item: InventoryItem, quantity: number) => void;
  onStatus: (item: InventoryItem, status: "CONSUMED" | "DISCARDED") => void; onAddToList: (item: InventoryItem) => void;
}) {
  const urgent = inventory.filter((item) => { const days = daysUntil(item.expiryDate); return days !== null && days <= 7; }).sort((a, b) => (daysUntil(a.expiryDate) ?? 9999) - (daysUntil(b.expiryDate) ?? 9999));
  const availableRecommendations = recommendations.filter((item) => !shopping.some((listItem) =>
    listItem.sourceInventoryId === item.id || listItem.name.trim().toLowerCase() === item.productName.trim().toLowerCase(),
  ));
  const expired = inventory.filter((item) => (daysUntil(item.expiryDate) ?? 0) < 0).length;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return (
    <>
      <section className="hero-card">
        <div className="hero-copy"><span className="eyebrow light">{greeting.toUpperCase()}, {user.toUpperCase()}</span><h1>Your pantry is<br /><em>looking fresh.</em></h1><p>{urgent.length ? `${urgent.length} ${urgent.length === 1 ? "item needs" : "items need"} attention this week.` : "Nothing is expiring in the next seven days."}</p></div>
        <button className="scan-hero" onClick={onScan}><span><Barcode /></span><strong>Scan item</strong><small>Add in seconds</small><ChevronRight /></button>
        <div className="hero-orbit orbit-one" /><div className="hero-orbit orbit-two" />
      </section>

      <section className="stats-grid" aria-label="Pantry summary">
        <button onClick={() => onTab("pantry")}><span className="stat-icon green"><Package /></span><span><strong>{inventory.length}</strong><small>Items at home</small></span><ChevronRight /></button>
        <button onClick={() => onTab("pantry")}><span className="stat-icon amber"><CalendarClock /></span><span><strong>{urgent.length}</strong><small>Expiring soon</small></span><ChevronRight /></button>
        <button onClick={() => onTab("shopping")}><span className="stat-icon coral"><ShoppingBasket /></span><span><strong>{shopping.filter((item) => !item.checked).length}</strong><small>On grocery list</small></span><ChevronRight /></button>
        <button onClick={() => onTab("pantry")}><span className="stat-icon red"><AlertTriangle /></span><span><strong>{expired}</strong><small>Already expired</small></span><ChevronRight /></button>
      </section>

      <section className="content-section">
        <div className="section-heading"><div><span className="eyebrow">USE THESE FIRST</span><h2>Expiring soon</h2></div><button className="text-button" onClick={() => onTab("pantry")}>View all <ArrowRight /></button></div>
        {urgent.length ? <div className="inventory-list compact">{urgent.slice(0, 3).map((item) => <InventoryCard key={item.id} item={item} onEdit={onEdit} onQuantity={onQuantity} onStatus={onStatus} onAddToList={onAddToList} />)}</div> : <Empty icon={Check} title="All clear" text="Nothing needs attention in the next seven days." />}
      </section>

      {availableRecommendations.length > 0 && <section className="low-stock-strip"><span className="stat-icon coral"><ListPlus /></span><div><strong>{availableRecommendations.length} restock {availableRecommendations.length === 1 ? "reminder" : "reminders"}</strong><p>Used-up and low-stock products are ready to review.</p></div><button onClick={() => onTab("shopping")}>Review <ArrowRight /></button></section>}
    </>
  );
}

function Empty({ icon: Icon, title, text }: { icon: typeof Package; title: string; text: string }) {
  return <div className="empty-state"><span><Icon /></span><h3>{title}</h3><p>{text}</p></div>;
}

function PantryView({ inventory, onAdd, onEdit, onQuantity, onStatus, onAddToList }: {
  inventory: InventoryItem[]; onAdd: () => void; onEdit: (item: InventoryItem) => void;
  onQuantity: (item: InventoryItem, quantity: number) => void; onStatus: (item: InventoryItem, status: "CONSUMED" | "DISCARDED") => void; onAddToList: (item: InventoryItem) => void;
}) {
  const [filter, setFilter] = useState<ExpiryFilter>("all");
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("All locations");
  const filtered = inventory.filter((item) => matchesExpiryFilter(item.expiryDate, filter))
    .filter((item) => location === "All locations" || item.location === location)
    .filter((item) => `${item.productName} ${item.brand ?? ""} ${item.category}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => (daysUntil(a.expiryDate) ?? 99999) - (daysUntil(b.expiryDate) ?? 99999));
  return (
    <section className="page-section">
      <div className="page-heading"><div><span className="eyebrow">SHARED INVENTORY</span><h1>Pantry</h1><p>{inventory.length} active items across your home.</p></div><button className="primary-button desktop-add" onClick={onAdd}><Plus /> Add item</button></div>
      <div className="inventory-toolbar">
        <label className="search-box"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search pantry" aria-label="Search pantry" /></label>
        <select value={location} onChange={(event) => setLocation(event.target.value)} aria-label="Filter by location"><option>All locations</option><option>Pantry</option><option>Fridge</option><option>Freezer</option></select>
      </div>
      <div className="filter-chips" aria-label="Filter by expiry">{expiryFilters.map((item) => <button key={item.id} className={filter === item.id ? "active" : ""} onClick={() => setFilter(item.id)}>{item.label}</button>)}</div>
      <div className="results-row"><strong>{filtered.length} {filtered.length === 1 ? "item" : "items"}</strong><span>Sorted by expiry</span></div>
      {filtered.length ? <div className="inventory-list">{filtered.map((item) => <InventoryCard key={item.id} item={item} onEdit={onEdit} onQuantity={onQuantity} onStatus={onStatus} onAddToList={onAddToList} />)}</div> : <Empty icon={Package} title="Nothing here" text="Try another filter or add a pantry item." />}
    </section>
  );
}

function ShoppingView({ items, recommendations, onAdd, onPurchase, onRemove, onRecommendation, onDismiss }: {
  items: ShoppingItem[]; recommendations: RecommendationItem[]; onAdd: (name: string, quantity: number, unit: string, sourceInventoryId?: string) => Promise<void>;
  onPurchase: (item: ShoppingItem) => void; onRemove: (item: ShoppingItem) => Promise<void>;
  onRecommendation: (item: RecommendationItem) => Promise<void>; onDismiss: (item: RecommendationItem) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [sourceInventoryId, setSourceInventoryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissalBusy, setDismissalBusy] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState<ProductHistoryResult[]>([]);
  const [searchSelection, setSearchSelection] = useState("");
  const visibleRecommendations = recommendations.filter((item) => !items.some((listItem) =>
    listItem.sourceInventoryId === item.id || listItem.name.trim().toLowerCase() === item.productName.trim().toLowerCase(),
  ));

  useEffect(() => {
    const query = name.trim();
    if (query.length < 2 || query.toLowerCase() === searchSelection) {
      setSearchResults([]);
      setSearchBusy(false);
      setSearchError("");
      return;
    }

    let cancelled = false;
    setSearchBusy(true);
    setSearchError("");
    const timer = window.setTimeout(async () => {
      try {
        const results = await searchProducts(query);
        if (!cancelled) setSearchResults(results);
      } catch {
        if (!cancelled) {
          setSearchResults([]);
          setSearchError("Could not search saved household products.");
        }
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [name, searchSelection]);

  function changeName(value: string) {
    setName(value);
    setUnit("pcs");
    setSourceInventoryId("");
    setSearchSelection("");
  }

  function selectProduct(product: ProductHistoryResult) {
    setName(product.productName);
    setUnit(product.unit || "pcs");
    setSourceInventoryId(product.id);
    setSearchSelection(product.productName.trim().toLowerCase());
    setSearchResults([]);
    setSearchError("");
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setSearchError("");
    try {
      await onAdd(name.trim(), 1, unit, sourceInventoryId || undefined);
      setName("");
      setUnit("pcs");
      setSourceInventoryId("");
      setSearchSelection("");
      setSearchResults([]);
    } catch {
      setSearchError("Could not add this product to the list.");
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(item: RecommendationItem) {
    setDismissalBusy(item.id);
    try { await onDismiss(item); }
    finally { setDismissalBusy(""); }
  }

  const recommendationDetail = (item: RecommendationItem) => item.recommendationReason === "used-up"
    ? "Used up · ready to restock"
    : `Low stock · ${item.quantity} ${item.unit} remaining`;
  return (
    <section className="page-section shopping-page">
      <div className="page-heading"><div><span className="eyebrow">ONE LIST FOR EVERYONE</span><h1>Grocery list</h1><p>{items.length} {items.length === 1 ? "thing" : "things"} to pick up.</p></div></div>
      <div className="quick-add-wrap">
        <form className="quick-add" onSubmit={submit}><ListPlus /><input value={name} onChange={(event) => changeName(event.target.value)} placeholder="Add milk, bananas, rice…" aria-label="New grocery item" aria-autocomplete="list" aria-expanded={searchResults.length > 0} /><button disabled={busy || !name.trim()}>{busy ? <LoaderCircle className="spin" /> : <Plus />}<span>Add</span></button></form>
        {searchBusy && <div className="quick-add-searching"><LoaderCircle className="spin" /> Searching saved products…</div>}
        {!searchBusy && searchResults.length > 0 && (
          <div className="product-history-results shopping-search-results" role="listbox" aria-label="Saved household products">
            <span>Saved household products</span>
            {searchResults.map((product) => (
              <button key={product.id} type="button" role="option" aria-selected="false" onClick={() => selectProduct(product)}>
                <span><strong>{product.productName}</strong><small>{[product.brand, product.category, product.unit].filter(Boolean).join(" · ")}</small></span>
                <em className={product.status.toLowerCase()}>{productStatusLabels[product.status]}</em>
              </button>
            ))}
          </div>
        )}
        {searchError && <div className="quick-add-search-error" role="alert">{searchError}</div>}
      </div>
      {visibleRecommendations.length > 0 && (
        <div className="suggestions">
          <div className="section-heading"><div><span className="eyebrow">SMART RESTOCK</span><h2>Recommended for your list</h2></div><span className="suggestion-hint">Dismissed items return after the next stock cycle</span></div>
          <div className="suggestion-row">
            {visibleRecommendations.map((item) => (
              <div className="suggestion-card" key={item.id}>
                <button className="suggestion-add" onClick={() => onRecommendation(item)} aria-label={`Add ${item.productName} to grocery list`}>
                  <span className="suggestion-plus"><Plus /></span><span><strong>{item.productName}</strong><small>{recommendationDetail(item)}</small></span>
                </button>
                <button className="suggestion-dismiss" disabled={dismissalBusy === item.id} onClick={() => dismiss(item)} aria-label={`Hide ${item.productName} recommendation`} title="Hide until restocked and low again">
                  {dismissalBusy === item.id ? <LoaderCircle className="spin" /> : <X />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="list-card">
        <div className="list-card-header"><strong>To buy</strong><span>Move purchased items into your pantry</span></div>
        {items.length ? items.map((item) => (
          <div className="shopping-row" key={item.id}>
            <div className="shopping-item-copy"><strong>{item.name}</strong><small>{item.quantity} {item.unit} · added by {item.createdBy}</small></div>
            <div className="shopping-row-actions">
              <button className="move-to-pantry" onClick={() => onPurchase(item)}><PackagePlus /> Add to pantry</button>
              <button className="icon-button small" onClick={() => onRemove(item)} aria-label={`Remove ${item.name}`}><Trash2 /></button>
            </div>
          </div>
        )) : <Empty icon={ShoppingBasket} title="Your list is clear" text="Add something above or choose a low-stock suggestion." />}
      </div>
      <div className="shopping-tip"><ShoppingBasket /><div><strong>From trolley to pantry</strong><p>Tap Add to pantry and we’ll prefill saved product details, then ask for the fresh pack’s expiry and storage location.</p></div></div>
    </section>
  );
}

function ActivityItemDetails({ activity, onClose }: { activity: ActivityItem; onClose: () => void }) {
  const item = activity.itemSnapshot;
  if (!item) return null;
  const expiry = item.expiryDate
    ? `${new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${item.expiryDate}T00:00:00Z`))} · ${item.expiryType === "use-by" ? "Use by" : "Best before"}`
    : "No expiry set";
  const specs = [
    ["Brand", item.brand || "Not set"],
    ["Category", item.category || "Other"],
    ["Quantity after use", `${item.quantity} ${item.unit}`],
    ["Stored in", item.location],
    ["Pack", item.isOpen ? "Open" : "Sealed"],
    ["Expiry", expiry],
    ["Low-stock alert", `${item.minimumQuantity} ${item.unit}`],
    ["Barcode", item.barcode || "Not set"],
  ];
  return (
    <div className="activity-detail-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="activity-detail-sheet" role="dialog" aria-modal="true" aria-labelledby="activity-detail-title">
        <header>
          <div><span className="eyebrow">USED ITEM DETAILS</span><h2 id="activity-detail-title">{item.productName}</h2><p>Saved when {activity.performedBy} used this item.</p></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close item details"><X /></button>
        </header>
        <div className="activity-detail-scroll">
          <div className="activity-product-summary">
            <span className="product-avatar">{item.imageUrl ? <img src={item.imageUrl} alt="" /> : item.productName.charAt(0).toUpperCase()}</span>
            <div><strong>{item.productName}</strong><small>{productStatusLabels[item.status]} · {activity.detail || "Used from pantry"}</small></div>
          </div>
          <dl className="activity-spec-grid">
            {specs.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>
          {item.notes && <div className="activity-notes"><span>Notes</span><p>{item.notes}</p></div>}
        </div>
        <footer><button type="button" className="primary-button" onClick={onClose}>Done</button></footer>
      </section>
    </div>
  );
}

function ActivityView({ activities, onExport, onUndo, undoingId }: {
  activities: ActivityItem[];
  onExport: () => void;
  onUndo: (activity: ActivityItem) => void;
  undoingId: string;
}) {
  const [selected, setSelected] = useState<ActivityItem | null>(null);
  const actionIcon = (action: string) => action === "discarded" ? Trash2 : action === "used" ? Utensils : action === "listed" || action === "hid recommendation for" ? ClipboardList : action === "restored" ? RotateCcw : Package;
  return (
    <section className="page-section">
      <div className="page-heading"><div><span className="eyebrow">HOUSEHOLD HISTORY</span><h1>Activity</h1><p>See what changed and who changed it.</p></div><button className="secondary-button desktop-add" onClick={onExport}><Download /> Export data</button></div>
      <div className="activity-list">
        {activities.length ? activities.map((item) => {
          const Icon = actionIcon(item.action);
          return <article key={item.id} className="activity-row">
            <span className={`activity-icon ${item.action}`}><Icon /></span>
            <div className="activity-copy"><p><strong>{item.performedBy}</strong> {item.action} <strong>{item.entityName}</strong></p>{item.detail && <small>{item.detail}</small>}</div>
            <div className="activity-row-meta">
              <time>{new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(new Date(item.createdAt))}</time>
              {((item.action === "used" && Boolean(item.itemSnapshot)) || item.canUndo) && <div className="activity-row-actions">
                {item.action === "used" && item.itemSnapshot && <button type="button" onClick={() => setSelected(item)}><Info /> Item specs</button>}
                {item.canUndo && <button type="button" className="undo-action" disabled={undoingId === item.id} onClick={() => onUndo(item)}>
                  {undoingId === item.id ? <LoaderCircle className="spin" /> : <RotateCcw />} Undo
                </button>}
              </div>}
            </div>
          </article>;
        }) : <Empty icon={Activity} title="No activity yet" text="Pantry and list changes will appear here." />}
      </div>
      <button className="secondary-button mobile-export" onClick={onExport}><Download /> Export household data</button>
      {selected && <ActivityItemDetails activity={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

export function App() {
  const { user, loading: authLoading, signOut } = useAuth();
  const [tab, setTab] = useState<Tab>("home");
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [shopping, setShopping] = useState<ShoppingItem[]>([]);
  const [recommendations, setRecommendations] = useState<RecommendationItem[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [purchased, setPurchased] = useState<ShoppingItem | null>(null);
  const [undoingActivityId, setUndoingActivityId] = useState("");
  const [toast, setToast] = useState("");

  const refresh = useCallback(async () => {
    if (!user) return;
    const [stock, list, history, suggested] = await Promise.all([listInventory(), listShopping(), listActivity(), listRecommendations()]);
    setInventory(stock); setShopping(list); setActivities(history); setRecommendations(suggested); setLoading(false);
  }, [user]);

  useEffect(() => { refresh().catch(() => { setToast("Could not load household data."); setLoading(false); }); }, [refresh]);
  useEffect(() => { if (!toast) return; const timer = window.setTimeout(() => setToast(""), 3200); return () => window.clearTimeout(timer); }, [toast]);

  const sortedInventory = useMemo(() => [...inventory].sort((a, b) => a.productName.localeCompare(b.productName)), [inventory]);

  if (authLoading) return <div className="app-loader"><span className="brand-mark large"><Leaf /></span><LoaderCircle className="spin" /></div>;
  if (!user) return <SignIn />;

  async function saveItem(input: InventoryInput, existing?: InventoryItem | null) {
    if (existing) await updateInventory(existing.id, { ...input, version: existing.version });
    else await createInventory(input, purchased?.sourceInventoryId);
    if (purchased) await removeShopping(purchased.id);
    setEditing(null); setPurchased(null); setFormOpen(false); setToast(existing ? "Pantry item updated." : "Added to the pantry."); await refresh();
  }
  async function changeQuantity(item: InventoryItem, quantity: number) { await updateInventory(item.id, { quantity, version: item.version }); await refresh(); }
  async function setStatus(item: InventoryItem, status: "CONSUMED" | "DISCARDED") {
    if (status === "CONSUMED") {
      const next = useOneFromStock(item.quantity);
      await updateInventory(item.id, { quantity: next.quantity, ...(next.consumed ? { status: "CONSUMED" as const } : {}), usedOne: true, version: item.version });
      setToast(next.consumed ? `${item.productName} is now used up.` : `Used one ${item.unit} of ${item.productName}. ${next.quantity} remain.`);
    } else {
      await updateInventory(item.id, { status, version: item.version });
      setToast("Marked as discarded.");
    }
    await refresh();
  }
  async function addRecommendation(item: InventoryItem) {
    await createShopping(item.productName, Math.max(item.minimumQuantity, 1), item.unit, item.id);
    setToast(`${item.productName} added to the list.`);
    await refresh();
  }
  async function dismissRecommendation(item: RecommendationItem) {
    await updateInventory(item.id, { recommendationDismissed: true, version: item.version });
    setToast(`${item.productName} hidden until it is restocked and runs low again.`);
    await refresh();
  }
  async function addShopping(name: string, quantity: number, unit: string, sourceInventoryId?: string) { await createShopping(name, quantity, unit, sourceInventoryId); await refresh(); }
  async function deleteShopping(item: ShoppingItem) { await removeShopping(item.id); await refresh(); }
  async function revertLatestActivity(activity: ActivityItem) {
    setUndoingActivityId(activity.id);
    try {
      await undoActivity(activity.id);
      setToast(`Restored ${activity.entityName} to its previous state.`);
      await refresh();
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Could not undo that activity.");
    } finally {
      setUndoingActivityId("");
    }
  }
  function selectTab(next: Tab) {
    setTab(next);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  }
  function openNew() { setEditing(null); setPurchased(null); setFormOpen(true); }
  function editItem(item: InventoryItem) { setEditing(item); setPurchased(null); setFormOpen(true); }
  function buyItem(item: ShoppingItem) { setPurchased(item); setEditing(null); setFormOpen(true); }
  function exportData() {
    const data = isDemoMode ? exportDemoData() : { inventory, shopping, activities, exportedAt: new Date().toISOString() };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const link = document.createElement("a"); link.href = url; link.download = `sapk-grocery-${todayInSydney()}.json`; link.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="app-shell">
      <aside className="desktop-rail">
        <div className="rail-brand"><span className="brand-mark"><Leaf /></span><span><strong>Sapk</strong><small>Grocery</small></span></div>
        <nav>{navItems.map(({ id, label, icon: Icon }) => <button key={id} className={tab === id ? "active" : ""} onClick={() => selectTab(id)}><Icon /><span>{label}</span>{id === "shopping" && shopping.length > 0 && <b>{shopping.length}</b>}</button>)}</nav>
        <div className="rail-user"><span>{user.charAt(0).toUpperCase()}</span><div><strong>{user}</strong><small>Household member</small></div><button onClick={signOut} aria-label="Sign out"><LogOut /></button></div>
      </aside>
      <div className="app-main">
        <header className="mobile-header"><div className="rail-brand"><span className="brand-mark"><Leaf /></span><span><strong>Sapk Grocery</strong><small>Shared pantry</small></span></div><button className="user-chip" onClick={signOut} aria-label="Sign out"><CircleUserRound /><span>{user}</span></button></header>
        <main className="page-content">
          {loading ? <div className="content-loader"><LoaderCircle className="spin" /><p>Opening your pantry…</p></div> : (
            <>
              {tab === "home" && <HomeView user={user} inventory={inventory} shopping={shopping} recommendations={recommendations} onScan={openNew} onTab={selectTab} onEdit={editItem} onQuantity={changeQuantity} onStatus={setStatus} onAddToList={addRecommendation} />}
              {tab === "pantry" && <PantryView inventory={sortedInventory} onAdd={openNew} onEdit={editItem} onQuantity={changeQuantity} onStatus={setStatus} onAddToList={addRecommendation} />}
              {tab === "shopping" && <ShoppingView items={shopping} recommendations={recommendations} onAdd={addShopping} onPurchase={buyItem} onRemove={deleteShopping} onRecommendation={addRecommendation} onDismiss={dismissRecommendation} />}
              {tab === "activity" && <ActivityView activities={activities} onExport={exportData} onUndo={revertLatestActivity} undoingId={undoingActivityId} />}
            </>
          )}
        </main>
      </div>
      <button className="mobile-fab" onClick={openNew} aria-label="Scan or add pantry item"><Barcode /></button>
      <nav className="bottom-nav" aria-label="Main navigation">{navItems.map(({ id, label, icon: Icon }) => <button key={id} className={tab === id ? "active" : ""} onClick={() => selectTab(id)}><Icon /><span>{label}</span>{id === "shopping" && shopping.length > 0 && <b>{shopping.length}</b>}</button>)}</nav>
      {formOpen && <ItemForm initial={editing} shoppingItem={purchased} onSave={saveItem} onClose={() => { setFormOpen(false); setEditing(null); setPurchased(null); }} />}
      {toast && <div className="toast" role="status"><Check />{toast}</div>}
    </div>
  );
}
